import { stateToProgress, statusToLabel, statusToPill } from './jobStatusMap'
import type { IngestJobState } from './jobStatusMap'
import type { IngestJobRow } from './mergeIngestJobs'
import type { Status } from '../../../components/primitives/types'

export type RunActionLabel = 'Add site' | 'Modify site' | 'Ingest'

export interface IngestRunRow {
  /** Stable group key (full groupId, or singleton job id). */
  id: string
  /** Short mono label for the Run column. */
  displayId: string
  /** Human-readable organization-unique name, absent on legacy rows. */
  runName?: string
  action: RunActionLabel
  siteId: string
  submittedAt: string
  state: IngestJobState
  progress: number
  message?: string
  jobs: IngestJobRow[]
}

function actionLabel(ingestRunAction: IngestJobRow['ingestRunAction']): RunActionLabel {
  if (ingestRunAction === 'add_site') return 'Add site'
  if (ingestRunAction === 'update_site') return 'Modify site'
  return 'Ingest'
}

function rollupState(jobs: IngestJobRow[]): IngestJobState {
  if (jobs.some((j) => j.state === 'error')) return 'error'
  if (jobs.every((j) => j.state === 'cancelled')) return 'cancelled'
  if (jobs.some((j) => j.state === 'awaiting_user')) return 'awaiting_user'
  if (jobs.some((j) => j.state === 'awaiting_rebuild')) return 'awaiting_rebuild'
  if (jobs.some((j) => j.state === 'awaiting_review')) return 'awaiting_review'
  if (jobs.some((j) => j.state === 'awaiting_turn')) return 'awaiting_turn'
  if (jobs.some((j) => j.ingestMode === 'diff-pending-apply' && j.state === 'done')) {
    return 'awaiting_user'
  }
  const active = jobs.some(
    (j) =>
      j.state === 'uploading' ||
      j.state === 'queued' ||
      j.state === 'processing' ||
      j.state === 'uploaded' ||
      j.state === 'classifying' ||
      j.state === 'classified' ||
      j.state === 'extracting' ||
      j.state === 'ingested' ||
      j.state === 'transforming' ||
      j.state === 'cancelling'
  )
  if (active) return 'processing'
  // A skipped job (nothing to do) is a successful outcome for the run, so a
  // run of done and skipped jobs reads as done rather than as whichever job
  // happened to come first.
  if (jobs.every((j) => j.state === 'done' || j.state === 'skipped')) {
    return jobs.some((j) => j.state === 'done') ? 'done' : 'skipped'
  }
  return jobs[0]?.state ?? 'queued'
}

function rollupMessage(jobs: IngestJobRow[], state: IngestJobState): string | undefined {
  if (state === 'error') {
    const failed = jobs.find((j) => j.state === 'error')
    return failed
      ? `Run stopped at a failure — ${failed.kind} “${failed.fileName}” failed.`
      : undefined
  }
  if (state === 'awaiting_user') {
    return 'A job in this run needs review before the ingest can finish.'
  }
  if (state === 'awaiting_rebuild') {
    return 'A mapped tabular job needs a config update before the ingest can finish.'
  }
  if (state === 'awaiting_review') {
    return 'A mapped tabular run is held until its flagged pages are reviewed. Nothing is committed yet.'
  }
  if (state === 'awaiting_turn') {
    return 'A mapped tabular run waits for earlier files from its intake to commit first. Nothing is committed yet.'
  }
  return undefined
}

/**
 * Group flat job rows into wizard runs. Jobs that share a `groupId` collapse
 * into one run; jobs without a group each become their own singleton run
 * (legacy rows).
 */
export function groupIngestRuns(jobs: IngestJobRow[]): IngestRunRow[] {
  const byKey = new Map<string, IngestJobRow[]>()
  for (const job of jobs) {
    const key = job.groupId?.trim() || job.id
    const list = byKey.get(key)
    if (list) list.push(job)
    else byKey.set(key, [job])
  }

  const runs: IngestRunRow[] = []
  for (const [id, members] of byKey) {
    const sorted = [...members].sort(
      (a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime()
    )
    const newest = [...members].sort(
      (a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
    )[0]!
    const state = rollupState(members)
    const progress =
      members.length === 0
        ? 0
        : Math.round(
            members.reduce((sum, j) => sum + (stateToProgress[j.state] ?? 0), 0) / members.length
          )
    const ingestRunAction = members.find((j) => j.ingestRunAction)?.ingestRunAction
    const runName = members.find((j) => j.runName?.trim())?.runName?.trim()
    // Prefer the uuid tail of ingest-run-/plc-upload- prefixes for display.
    const bare = id.replace(/^(ingest-run-|plc-upload-)/, '')
    runs.push({
      id,
      displayId: bare.slice(0, 8),
      ...(runName ? { runName } : {}),
      action: actionLabel(ingestRunAction),
      siteId: newest.siteId?.trim() ? newest.siteId : '—',
      submittedAt: newest.submittedAt,
      state,
      progress,
      message: rollupMessage(members, state),
      jobs: sorted
    })
  }

  return runs.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
}

export function runStatusLabel(run: IngestRunRow): string {
  if (run.state === 'awaiting_user') return 'review needed'
  if (run.state === 'processing') return 'running'
  if (run.state === 'done') return 'complete'
  if (run.state === 'error') return 'failed'
  return statusToLabel[run.state] ?? run.state
}

export function runStatusPill(run: IngestRunRow): Status {
  if (run.state === 'awaiting_user') return 'warn'
  if (run.state === 'done') return 'run'
  return statusToPill[run.state]
}

/**
 * The run that holds `jobId` and the job itself, for `/ingest?job=<id>` links
 * (notifications point at a job, the table expands its run).
 */
export function findRunForJob(
  runs: readonly IngestRunRow[],
  jobId: string
): { runId: string; job: IngestJobRow } | null {
  for (const run of runs) {
    const job = run.jobs.find((j) => j.id === jobId)
    if (job) return { runId: run.id, job }
  }
  return null
}
